import type { FaviconPaint, FaviconRenderer as PaintsRenderer } from '@/lib/headCue';
import { headCue } from '@/lib/headCue';
import { composeFavicon } from '@/lib/headCue/favicon';
import { type StageCue, TONE_TOKENS, titlePrefix } from './stage';

/**
 * Composites the stage dot onto the app icon; returns a data: URL, or null.
 * Production passes none: the head-cue composer's default canvas compose draws
 * the dot (stageDotPaint) after every other layer's paint. A test injects one.
 */
export type FaviconRenderer = (color: string, ring: string) => Promise<string | null>;

const ORIG_CONTENT = 'data-lvdr-orig-content';

interface Applied {
  cue: StageCue;
  renderFavicon: FaviconRenderer | undefined;
  /** Resolved token colours, kept so a theme change can tell whether they moved. */
  fill: string | null;
  mark: string | null;
  ring: string | null;
}

let current: Applied | null = null;
let unsubscribeHead: (() => void) | null = null;
let themeObserver: MutationObserver | null = null;
/** True while the head-cue renderer is the adapter this module installed. */
let ownsRenderer = false;

/** A palette token as a CSS colour, or null when the stylesheet has not defined it. */
export function tokenColor(token: string): string | null {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--${token}`).trim();
  return v ? `hsl(${v.split(/\s+/).join(', ')})` : null;
}

const themeMetas = () => document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');

/** Theme-colour is this module's own; the head-cue composer owns title and icons. */
function reapplyThemeColor(): void {
  if (!current?.fill) return;
  for (const m of themeMetas()) {
    if (!m.hasAttribute(ORIG_CONTENT)) m.setAttribute(ORIG_CONTENT, m.content);
    if (m.content !== current.fill) m.content = current.fill;
  }
}

/** The stage dot, bottom-right: a background-coloured ring around the tone mark. */
export function stageDotPaint(color: string, ring: string): FaviconPaint {
  return (ctx, size) => {
    const k = size / 64;
    ctx.beginPath();
    ctx.arc(48 * k, 48 * k, 15 * k, 0, 2 * Math.PI);
    ctx.fillStyle = ring;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(48 * k, 48 * k, 11 * k, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  };
}

/**
 * Test seam only: turns an injected `(color, ring)` renderer into a head-cue
 * renderer. Its image is the base; every other layer's paint is drawn over it
 * (so the dot is not last on this path — production never takes it). With the
 * stage dot the only paint, the injected renderer's URL goes out unchanged.
 */
function adapt(
  renderFavicon: FaviconRenderer,
  color: string,
  ring: string,
  dot: FaviconPaint,
): PaintsRenderer {
  return async (paints) => {
    const base = await renderFavicon(color, ring);
    const others = paints.filter((p) => p !== dot);
    if (!base || others.length === 0) return base;
    return (await composeFavicon(others, base)) ?? base;
  };
}

/**
 * The app theme flips `.dark` on <html> (useApplyTheme) without touching the
 * stage store. The stripe and the chip follow through CSS variables; the
 * theme-colour and the favicon dot hold resolved colours, so re-resolve them.
 * Re-apply only when a colour actually moved: useApplyTheme re-writes the same
 * class on every mount, which would otherwise redraw the favicon each time.
 */
function onThemeChange(): void {
  if (!current) return;
  const { cue, renderFavicon, fill, mark, ring } = current;
  const t = TONE_TOKENS[cue.tone];
  const unchanged =
    tokenColor(t.fill) === fill && tokenColor(t.mark) === mark && tokenColor('background') === ring;
  if (!unchanged) applyStageMarker(cue, renderFavicon);
}

/**
 * Resolves once the page has loaded and the main thread has then gone idle,
 * i.e. after React has finished hydrating, Next's metadata included. The hint
 * path (StageMarker, before the server answers) waits for it before it touches
 * <link rel="icon">: React 19 finds a server-rendered hoisted link by its href
 * while hydrating, so a link already pointed at the data: URL is not found and
 * React inserts a duplicate. Measured in a real browser (verify-goal-cues L1):
 * 8 icon links on every reload with a hint, and 4 when the favicon landed
 * after hydration. Safari has no requestIdleCallback; a short timeout after
 * `load` stands in.
 */
export function afterHydration(): Promise<void> {
  return new Promise((resolve) => {
    const idle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => resolve(), { timeout: 1000 });
      } else {
        setTimeout(resolve, 50);
      }
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  });
}

/**
 * Applies every stage cue for `cue` and keeps them applied. Title prefix and
 * favicon are the head-cue composer's: this registers the `stage` layer, whose
 * dot is painted after every other layer (`faviconLast`). Tone, theme-colour
 * and a class observer on <html> (a theme toggle re-resolves the colours) stay
 * here; theme-colour is re-applied from the composer's one head observer when
 * Next restores a meta attribute, and writes only on a difference.
 *
 * `renderFavicon` is a test seam: when given, it renders the stage image and
 * the composer's other paints go over it. `faviconGate`, when given, holds the
 * icon render (and so every icon-link write) until it resolves; title, tone
 * and theme-colour apply at once. A render superseded while it waited is
 * skipped, not run.
 */
export function applyStageMarker(
  cue: StageCue,
  renderFavicon?: FaviconRenderer,
  faviconGate?: Promise<unknown>,
): void {
  const t = TONE_TOKENS[cue.tone];
  const mark = tokenColor(t.mark);
  const ring = tokenColor('background');
  current = { cue, renderFavicon, fill: tokenColor(t.fill), mark, ring };
  const d = document.documentElement;
  // Write only on a difference: the pre-hydration script has usually set it
  // already, and StageMarker's no-flicker test asserts no mutation on it.
  if (d.getAttribute('data-lvdr-tone') !== cue.tone) d.setAttribute('data-lvdr-tone', cue.tone);
  reapplyThemeColor();
  unsubscribeHead ??= headCue.onHeadMutation(reapplyThemeColor);
  if (!themeObserver) {
    themeObserver = new MutationObserver(onThemeChange);
    themeObserver.observe(d, { attributes: true, attributeFilter: ['class'] });
  }
  // Without resolved colours there is no dot: the layer carries no paint.
  const dot = mark && ring ? stageDotPaint(mark, ring) : undefined;
  if (renderFavicon && dot && mark && ring) {
    headCue.setFaviconRenderer(adapt(renderFavicon, mark, ring, dot));
    ownsRenderer = true;
  } else if (ownsRenderer) {
    headCue.setFaviconRenderer(null);
    ownsRenderer = false;
  }
  headCue.setLayer(
    {
      id: 'stage',
      order: 0,
      titlePrefix: titlePrefix(cue),
      ...(dot ? { favicon: dot } : {}),
      faviconLast: true,
    },
    faviconGate ? { faviconGate } : {},
  );
}

/** Removes every cue and restores the originals it saved. */
export function clearStageMarker(): void {
  current = null;
  unsubscribeHead?.();
  unsubscribeHead = null;
  themeObserver?.disconnect();
  themeObserver = null;
  document.documentElement.removeAttribute('data-lvdr-tone');
  // The composer writes the title only when a prefix is there to strip: every
  // prod page load reaches this, and an unconditional write would replace the
  // <title> text node for nothing.
  headCue.removeLayer('stage');
  if (ownsRenderer) {
    headCue.setFaviconRenderer(null);
    ownsRenderer = false;
  }
  for (const m of themeMetas()) {
    if (!m.hasAttribute(ORIG_CONTENT)) continue;
    m.content = m.getAttribute(ORIG_CONTENT) ?? '';
    m.removeAttribute(ORIG_CONTENT);
  }
}
